import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  CheckCircle, CreditCard, Users, Heart, Star, 
  Loader2, ArrowRight, Gift, Lock, Sparkles 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl();

const MembershipPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStatus();
    } else {
      setStatusLoading(false);
    }
  }, [isAuthenticated]);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/membership' } } });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const isAlreadyMember = status?.subscription_active;

  const benefits = [
    {
      icon: Users,
      title: i18n.language === 'fr' ? 'Rencontres exclusives' : 'Exclusive meetups',
      description: i18n.language === 'fr' 
        ? 'Accédez à des rencontres entre passionnés organisées par nos guides'
        : 'Access meetups between enthusiasts organized by our guides'
    },
    {
      icon: Heart,
      title: i18n.language === 'fr' ? 'Communauté authentique' : 'Authentic community',
      description: i18n.language === 'fr' 
        ? 'Rejoignez une communauté de voyageurs partageant vos passions'
        : 'Join a community of travelers sharing your passions'
    },
    {
      icon: Star,
      title: i18n.language === 'fr' ? 'Expériences gratuites' : 'Free experiences',
      description: i18n.language === 'fr' 
        ? 'Participez gratuitement aux expériences proposées par nos guides passionnés'
        : 'Participate for free in experiences offered by our passionate guides'
    },
    {
      icon: Gift,
      title: i18n.language === 'fr' ? 'Soutenez les guides' : 'Support guides',
      description: i18n.language === 'fr' 
        ? 'Votre adhésion aide à soutenir notre communauté de guides locaux'
        : 'Your membership helps support our community of local guides'
    }
  ];

  return (
    <main className="min-h-screen pt-20 pb-16" data-testid="membership-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-purple-100 to-white">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-200 text-purple-800 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {i18n.language === 'fr' ? 'Adhésion annuelle' : 'Annual membership'}
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {i18n.language === 'fr' 
                ? 'Devenez membre XPerience'
                : 'Become an XPerience member'}
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
              {i18n.language === 'fr' 
                ? 'Nos guides proposent des rencontres gratuites : ateliers cuisine, balades découvertes, échanges culturels... Ces moments de partage sont réservés aux membres XPerience.'
                : 'Our guides offer free meetups: cooking workshops, discovery walks, cultural exchanges... These sharing moments are reserved for XPerience members.'}
            </p>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 max-w-xl mx-auto mb-8 text-left">
              <p className="text-sm text-orange-800">
                <strong>{i18n.language === 'fr' ? '💡 Bon à savoir :' : '💡 Good to know:'}</strong>{' '}
                {i18n.language === 'fr' 
                  ? 'Les expériences payantes restent accessibles à tous, sans abonnement. L\'adhésion débloque uniquement l\'accès aux rencontres gratuites proposées par nos guides bénévoles.'
                  : 'Paid experiences remain accessible to everyone, without subscription. The membership only unlocks access to free meetups offered by our volunteer guides.'}
              </p>
            </div>

            {/* Pricing Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-block bg-white rounded-3xl shadow-xl p-8 border-2 border-purple-200"
            >
              {statusLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              ) : isAlreadyMember ? (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-green-700 mb-2">
                    {i18n.language === 'fr' ? 'Vous êtes membre !' : 'You are a member!'}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {i18n.language === 'fr' 
                      ? 'Vous avez accès à toutes les expériences gratuites.'
                      : 'You have access to all free experiences.'}
                  </p>
                  {status?.subscription_expires_at && (
                    <p className="text-sm text-gray-500">
                      {i18n.language === 'fr' ? 'Expire le' : 'Expires on'}{' '}
                      {new Date(status.subscription_expires_at).toLocaleDateString(
                        i18n.language === 'fr' ? 'fr-FR' : 'en-US'
                      )}
                    </p>
                  )}
                  <Button 
                    onClick={() => navigate('/explore')}
                    className="mt-6 bg-purple-600 hover:bg-purple-700"
                  >
                    {i18n.language === 'fr' ? 'Explorer les expériences' : 'Explore experiences'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <div className="text-5xl font-bold text-purple-700">49€</div>
                    <div className="text-muted-foreground">
                      {i18n.language === 'fr' ? 'par an' : 'per year'}
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-8 text-left">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{i18n.language === 'fr' ? 'Accès illimité aux rencontres gratuites (ateliers, balades, échanges...)' : 'Unlimited access to free meetups (workshops, walks, exchanges...)'}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{i18n.language === 'fr' ? 'Rencontrez des passionnés qui partagent bénévolement' : 'Meet enthusiasts who share voluntarily'}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{i18n.language === 'fr' ? 'Soutenez notre communauté de guides indépendants' : 'Support our community of independent guides'}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{i18n.language === 'fr' ? 'Annulation possible à tout moment' : 'Cancel anytime'}</span>
                    </li>
                  </ul>
                  
                  <Button 
                    onClick={handleSubscribe}
                    disabled={loading}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-lg py-6"
                    data-testid="subscribe-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-5 h-5 mr-2" />
                    )}
                    {i18n.language === 'fr' ? 'Devenir membre' : 'Become a member'}
                  </Button>
                  
                  <p className="text-xs text-muted-foreground mt-4 text-center">
                    {i18n.language === 'fr' 
                      ? 'Paiement sécurisé par Stripe. Annulation possible à tout moment.'
                      : 'Secure payment by Stripe. Cancel anytime.'}
                  </p>
                </>
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
          {i18n.language === 'fr' ? 'Avantages membres' : 'Member benefits'}
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 border hover:shadow-lg transition-shadow"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                <benefit.icon className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold mb-2">{benefit.title}</h3>
              <p className="text-sm text-muted-foreground">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
            {i18n.language === 'fr' ? 'Questions fréquentes' : 'Frequently asked questions'}
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border">
              <h3 className="font-semibold mb-2">
                {i18n.language === 'fr' 
                  ? 'Qu\'est-ce qu\'une expérience gratuite ?'
                  : 'What is a free experience?'}
              </h3>
              <p className="text-muted-foreground">
                {i18n.language === 'fr' 
                  ? 'Ce sont des expériences proposées par nos guides passionnés qui partagent leur savoir et leur région gratuitement. Elles incluent des rencontres entre vanlifers, des randonnées entre passionnés, des échanges culturels, etc.'
                  : 'These are experiences offered by our passionate guides who share their knowledge and region for free. They include vanlife meetups, hikes between enthusiasts, cultural exchanges, etc.'}
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border">
              <h3 className="font-semibold mb-2">
                {i18n.language === 'fr' 
                  ? 'Puis-je annuler mon adhésion ?'
                  : 'Can I cancel my membership?'}
              </h3>
              <p className="text-muted-foreground">
                {i18n.language === 'fr' 
                  ? 'Oui, vous pouvez annuler à tout moment. Votre adhésion reste active jusqu\'à la fin de la période payée (1 an).'
                  : 'Yes, you can cancel anytime. Your membership remains active until the end of the paid period (1 year).'}
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border">
              <h3 className="font-semibold mb-2">
                {i18n.language === 'fr' 
                  ? 'Les expériences payantes sont-elles incluses ?'
                  : 'Are paid experiences included?'}
              </h3>
              <p className="text-muted-foreground">
                {i18n.language === 'fr' 
                  ? 'Non, l\'adhésion donne accès uniquement aux expériences gratuites. Les expériences payantes sont accessibles à tous et facturées séparément avec une commission de 15%.'
                  : 'No, the membership gives access only to free experiences. Paid experiences are accessible to everyone and billed separately with a 15% commission.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default MembershipPage;
